// src/pages/BookingManagement.js
// Public page — no login required. Token in URL serves as auth.
// Route: /booking/manage/:bookingId/:token
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import CancelConfirmation from '../components/booking/CancelConfirmation';
import RescheduleFlow from '../components/booking/RescheduleFlow';
import LogoDark from '../assets/divine-frameworks-logo.svg';

const REGION = 'us-west1';

const formatDateTime = (dateTime, timezone) => {
  try {
    const date = dateTime?._seconds
      ? new Date(dateTime._seconds * 1000)
      : new Date(dateTime);
    return date.toLocaleString('en-US', {
      timeZone: timezone || 'UTC',
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return String(dateTime);
  }
};

const BookingManagement = () => {
  const { bookingId, token } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState(searchParams.get('action') === 'cancel' ? 'cancel_pending' : 'details');
  const [cancelResult, setCancelResult] = useState(null);
  const [rescheduleResult, setRescheduleResult] = useState(null);

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        const functions = getFunctions(undefined, REGION);
        const getBooking = httpsCallable(functions, 'getBookingByToken');
        const result = await getBooking({ bookingId, token });
        setBooking(result.data);

        // If already cancelled, show that state
        if (result.data.status === 'cancelled') {
          setView('cancelled');
          setCancelResult({
            refundAmount: result.data.refundAmount,
            refundStatus: result.data.refundStatus,
          });
        } else if (searchParams.get('action') === 'cancel') {
          // Auto-open cancel flow when arriving from proposal page
          setView('cancel');
        }
      } catch (err) {
        const msg = err.message || 'Unable to load booking';
        if (msg.includes('not found') || msg.includes('Invalid token')) {
          setError('invalid');
        } else if (msg.includes('already passed')) {
          setError('past');
        } else {
          setError('generic');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchBooking();
  }, [bookingId, token]);

  const handleCancelled = (result) => {
    setCancelResult(result);
    setView('cancelled');
  };

  // ── Loading ──
  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <div style={styles.spinner} />
            <p style={{ color: '#6b7280', fontSize: '15px', marginTop: '16px' }}>Loading your booking...</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Error States ──
  if (error) {
    const messages = {
      invalid: "This link is no longer valid. If you need help with your booking, please contact your practitioner directly.",
      past: "This session has already taken place.",
      generic: "Something went wrong. Please try again or contact support@divineframeworks.com.",
    };
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.logoContainer} onClick={() => navigate('/')}>
            <img src={LogoDark} alt="Divine Frameworks" style={styles.logo} />
            <span style={styles.logoText}>Divine Frameworks</span>
          </div>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>
              {error === 'past' ? '⏰' : '🔗'}
            </div>
            <p style={{ color: '#374151', fontSize: '16px', lineHeight: 1.6, maxWidth: '400px', margin: '0 auto' }}>
              {messages[error]}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Cancelled State ──
  if (view === 'cancelled') {
    const refundDollars = cancelResult?.refundAmount
      ? (cancelResult.refundAmount / 100).toFixed(2)
      : null;

    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.logoContainer} onClick={() => navigate('/')}>
            <img src={LogoDark} alt="Divine Frameworks" style={styles.logo} />
            <span style={styles.logoText}>Divine Frameworks</span>
          </div>
          <div style={{ textAlign: 'center', padding: '40px 0', animation: 'bmFadeIn 0.4s ease' }}>
            <div style={{
              width: '56px', height: '56px', borderRadius: '50%',
              background: '#ECFDF5', display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px', fontSize: '24px',
            }}>✓</div>
            <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#111827', marginBottom: '12px' }}>
              Your session has been cancelled
            </h2>
            {refundDollars && cancelResult.refundAmount > 0 && cancelResult.refundStatus !== 'failed' && (
              <p style={{ fontSize: '15px', color: '#374151', lineHeight: 1.6, marginBottom: '8px' }}>
                Your refund of <strong>${refundDollars}</strong> will appear on your original payment method within 5–10 business days.
              </p>
            )}
            {cancelResult?.refundStatus === 'failed' && (
              <p style={{ fontSize: '14px', color: '#92400E', lineHeight: 1.6, marginBottom: '8px' }}>
                Refund processing — contact support@divineframeworks.com if not received within 10 business days.
              </p>
            )}
            <div style={{ marginTop: '32px' }}>
              <button
                onClick={() => navigate('/directory')}
                style={styles.primaryButton}
              >Browse practitioners</button>
            </div>
            <div style={{
              marginTop: '32px', padding: '16px 20px',
              background: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB',
            }}>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                Want to see all your sessions in one place?{' '}
                <span
                  onClick={() => navigate('/login')}
                  style={{ color: '#7c3aed', fontWeight: 500, cursor: 'pointer' }}
                >Log in or create an account →</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Cancel Confirmation View ──
  if (view === 'cancel') {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.logoContainer} onClick={() => navigate('/')}>
            <img src={LogoDark} alt="Divine Frameworks" style={styles.logo} />
            <span style={styles.logoText}>Divine Frameworks</span>
          </div>
          <CancelConfirmation
            booking={booking}
            bookingId={bookingId}
            token={token}
            onCancelled={handleCancelled}
            onBack={() => setView('details')}
          />
        </div>
      </div>
    );
  }

  // ── Reschedule View ──
  if (view === 'reschedule') {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.logoContainer} onClick={() => navigate('/')}>
            <img src={LogoDark} alt="Divine Frameworks" style={styles.logo} />
            <span style={styles.logoText}>Divine Frameworks</span>
          </div>
          <RescheduleFlow
            booking={booking}
            bookingId={bookingId}
            token={token}
            onRescheduled={(result) => {
              setRescheduleResult(result);
              setView('rescheduled');
            }}
            onBack={() => setView('details')}
          />
        </div>
        <style>{`
          @keyframes bmFadeIn {
            from { opacity: 0; transform: translateY(8px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    );
  }

  // ── Rescheduled Confirmation View ──
  if (view === 'rescheduled') {
    const newFormatted = rescheduleResult?.newDateTime
      ? formatDateTime(rescheduleResult.newDateTime, booking.timezone)
      : '';
    const adj = rescheduleResult?.priceAdjustmentCents || 0;

    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.logoContainer} onClick={() => navigate('/')}>
            <img src={LogoDark} alt="Divine Frameworks" style={styles.logo} />
            <span style={styles.logoText}>Divine Frameworks</span>
          </div>
          <div style={{ textAlign: 'center', padding: '40px 0', animation: 'bmFadeIn 0.4s ease' }}>
            <div style={{
              width: '56px', height: '56px', borderRadius: '50%',
              background: '#ECFDF5', display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px', fontSize: '24px',
            }}>✓</div>
            <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#111827', marginBottom: '12px' }}>
              Your session has been rescheduled
            </h2>
            <p style={{ fontSize: '17px', fontWeight: 600, color: '#111827', marginBottom: '4px' }}>
              {newFormatted}
            </p>
            <p style={{ fontSize: '15px', color: '#6b7280', marginBottom: '16px' }}>
              {rescheduleResult?.newService?.name || booking.service?.name} with {booking.practitionerName}
            </p>
            {adj < 0 && (
              <p style={{ fontSize: '14px', color: '#065F46', marginBottom: '8px' }}>
                Refund of ${(Math.abs(adj) / 100).toFixed(2)} will appear within 5–10 business days.
              </p>
            )}
            {adj > 0 && (
              <p style={{ fontSize: '14px', color: '#92400E', marginBottom: '8px' }}>
                Additional charge of ${(adj / 100).toFixed(2)} applies.
              </p>
            )}
            {rescheduleResult?.wasFreeReschedule && (
              <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
                Free reschedule used (1 of 1)
              </p>
            )}
            <div style={{ marginTop: '24px' }}>
              <button
                onClick={() => window.location.reload()}
                style={styles.primaryButton}
              >View your booking</button>
            </div>
            <div style={{
              marginTop: '32px', padding: '16px 20px',
              background: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB',
            }}>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                Want to see all your sessions in one place?{' '}
                <span
                  onClick={() => navigate('/login')}
                  style={{ color: '#7c3aed', fontWeight: 500, cursor: 'pointer' }}
                >Log in or create an account →</span>
              </p>
            </div>
          </div>
        </div>
        <style>{`
          @keyframes bmFadeIn {
            from { opacity: 0; transform: translateY(8px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    );
  }

  // ── Booking Details View ──
  const formattedDate = formatDateTime(booking.dateTime, booking.timezone);
  const priceDisplay = booking.service?.price ? `$${booking.service.price}` : 'Free';

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.logoContainer} onClick={() => navigate('/')}>
            <img src={LogoDark} alt="Divine Frameworks" style={styles.logo} />
            <span style={styles.logoText}>Divine Frameworks</span>
          </div>

        <h1 style={{ fontSize: '20px', fontWeight: 600, color: '#111827', marginBottom: '24px' }}>
          Your Booking
        </h1>

        {/* Booking card */}
        <div style={styles.card}>
          <div style={{ marginBottom: '20px' }}>
            <p style={{ fontSize: '18px', fontWeight: 600, color: '#111827', marginBottom: '4px' }}>
              {booking.service?.name || 'Session'}
            </p>
            <p style={{ fontSize: '15px', color: '#6b7280' }}>
              with {booking.practitionerName}
            </p>
          </div>

          <div style={styles.detailRow}>
            <span style={styles.detailLabel}>Date & Time</span>
            <span style={styles.detailValue}>{formattedDate}</span>
          </div>
          <div style={styles.detailRow}>
            <span style={styles.detailLabel}>Duration</span>
            <span style={styles.detailValue}>{booking.service?.duration || 60} minutes</span>
          </div>
          <div style={styles.detailRow}>
            <span style={styles.detailLabel}>Price</span>
            <span style={styles.detailValue}>{priceDisplay}</span>
          </div>
          <div style={{ ...styles.detailRow, borderBottom: 'none' }}>
            <span style={styles.detailLabel}>Status</span>
            <span style={{
              fontSize: '13px', fontWeight: 600, padding: '3px 10px', borderRadius: '12px',
              background: booking.status === 'reschedule_proposed' ? '#FEF3C7' : '#ECFDF5',
              color: booking.status === 'reschedule_proposed' ? '#92400E' : '#065F46',
            }}>{booking.status === 'reschedule_proposed' ? 'Reschedule proposed' : 'Confirmed'}</span>
          </div>

          {booking.zoomJoinUrl && (
            <div style={{ marginTop: '16px', textAlign: 'center' }}>
              <a
                href={booking.zoomJoinUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block', padding: '10px 24px',
                  background: '#bb9749', color: '#fff', borderRadius: '6px',
                  textDecoration: 'none', fontSize: '14px', fontWeight: 500,
                }}
              >Join on Zoom</a>
            </div>
          )}
        </div>

        {/* Proposal banner */}
        {booking.status === 'reschedule_proposed' && booking.rescheduleProposal?.status === 'pending' && (
          <div style={{
            marginTop: '20px', padding: '16px 20px', borderRadius: '10px',
            background: '#FFFBEB', border: '1px solid #FDE68A',
          }}>
            <p style={{ fontSize: '14px', fontWeight: 600, color: '#92400E', margin: '0 0 4px' }}>
              {booking.practitionerName} has proposed rescheduling this session
            </p>
            <p style={{ fontSize: '13px', color: '#92400E', margin: '0 0 12px' }}>
              Proposed: {formatDateTime(booking.rescheduleProposal.proposedDateTime, booking.timezone)}
            </p>
            <button
              onClick={() => navigate(`/booking/proposal/${bookingId}/${token}`)}
              style={{ ...styles.primaryButton, padding: '10px 20px', fontSize: '14px' }}
            >Respond to proposal</button>
          </div>
        )}

        {/* Counter-proposal pending banner */}
        {booking.status === 'reschedule_proposed' && booking.rescheduleProposal?.status === 'counter_proposed' && (
          <div style={{
            marginTop: '20px', padding: '16px 20px', borderRadius: '10px',
            background: '#FEF3C7', border: '1px solid #FDE68A',
          }}>
            <p style={{ fontSize: '14px', fontWeight: 600, color: '#92400E', margin: '0 0 4px' }}>
              Your suggested time has been sent
            </p>
            <p style={{ fontSize: '13px', color: '#92400E', margin: 0 }}>
              Waiting for {booking.practitionerName} to respond.
            </p>
          </div>
        )}

        {/* Action buttons */}
        <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {booking.status !== 'reschedule_proposed' && (
            <button
              onClick={() => setView('reschedule')}
              style={styles.primaryButton}
            >Reschedule</button>
          )}
          <button
            onClick={() => setView('cancel')}
            style={styles.dangerButton}
          >Cancel this session</button>
        </div>
      </div>

      <style>{`
        @keyframes bmFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes bmSpin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    background: '#F9FAFB',
    display: 'flex',
    justifyContent: 'center',
    padding: '40px 20px',
    fontFamily: "'Montserrat', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  container: {
    width: '100%',
    maxWidth: '520px',
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '32px',
    cursor: 'pointer',
  },
  logo: {
    height: '48px',
    width: 'auto',
  },
  logoText: {
    fontSize: '22px',
    fontWeight: 700,
    color: '#1f2937',
    fontFamily: "'Montserrat', sans-serif",
  },
  card: {
    background: '#fff',
    border: '1px solid #E5E7EB',
    borderRadius: '12px',
    padding: '24px',
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0',
    borderBottom: '1px solid #F3F4F6',
  },
  detailLabel: {
    fontSize: '14px',
    color: '#6b7280',
  },
  detailValue: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#111827',
    textAlign: 'right',
  },
  primaryButton: {
    width: '100%',
    padding: '14px 24px',
    background: '#bb9749',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Montserrat', sans-serif",
    transition: 'background 0.2s ease',
  },
  dangerButton: {
    width: '100%',
    padding: '14px 24px',
    background: 'transparent',
    color: '#DC2626',
    border: '1px solid #FCA5A5',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: "'Montserrat', sans-serif",
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '3px solid #E5E7EB',
    borderTopColor: '#7c3aed',
    borderRadius: '50%',
    animation: 'bmSpin 0.8s linear infinite',
    margin: '0 auto',
  },
};

export default BookingManagement;
